"use client"

import Link from "next/link"
import Image from "next/image"
import { usePathname } from "next/navigation"
import { useState } from "react"
import { Menu } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { cn } from "@/lib/utils"
import { useTranslations } from "@/lib/i18n/client"
import { LanguageSelector } from "@/components/public/LanguageSelector"
import type { DomainConfig } from "@/lib/domain"

function InstagramIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
    </svg>
  )
}

export function Header({ domainConfig }: { domainConfig: DomainConfig }) {
  const pathname = usePathname()
  const [mobileOpen, setMobileOpen] = useState(false)
  const { t } = useTranslations()

  const navLinks = [
    { href: "/aeronaves", label: domainConfig.catalogTitle },
    { href: "/sobre", label: t.common.about },
    { href: "/financiamento", label: t.common.financing },
    { href: "/contato", label: t.common.contact },
  ]

  // Intrinsic dimensions tuned to actual rendered height at retina 2x.
  // Header maxes at h-24 (96px) → 192px; drawer renders at h-8 (32px) → 64px.
  // Avoids over-fetching the full-resolution logo (was 756/1402px wide).
  const logoRatio = domainConfig.logoWidth / domainConfig.logoHeight
  const headerLogoH = 192
  const headerLogoW = Math.round(headerLogoH * logoRatio)
  const drawerLogoH = 64
  const drawerLogoW = Math.round(drawerLogoH * logoRatio)

  return (
    <header className="sticky top-0 z-40 border-b-[0.5px] border-navy-800 bg-navy-900">
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 md:h-28">
        {/* Logo */}
        <Link href="/" className="flex items-center gap-2">
          <Image
            src={domainConfig.logo}
            alt={domainConfig.siteName}
            width={headerLogoW}
            height={headerLogoH}
            className="h-14 w-auto md:h-24"
            priority
          />
        </Link>

        {/* Desktop nav */}
        <nav aria-label={t.common.mainNavigation} className="hidden items-center gap-8 md:flex">
          {navLinks.map((link) => {
            const isActive = pathname === link.href || pathname.startsWith(link.href + "/")
            return (
              <Link
                key={link.href}
                href={link.href}
                data-active={isActive || undefined}
                className={cn(
                  "nav-link text-sm font-medium transition-colors",
                  isActive
                    ? "text-white"
                    : "text-slate-300 hover:text-white"
                )}
              >
                {link.label}
              </Link>
            )
          })}
          <LanguageSelector />
          <a
            href={domainConfig.instagramUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t.common.instagramAriaLabel}
            className="inline-flex size-10 items-center justify-center rounded-lg text-slate-300 transition-colors hover:text-white"
          >
            <InstagramIcon className="h-5 w-5" />
          </a>
        </nav>

        {/* Mobile actions */}
        <div className="flex items-center gap-2 md:hidden">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileOpen(true)}
            aria-label={t.common.openMenu}
            className="size-12 text-white hover:bg-white/10 hover:text-white"
          >
            <Menu className="h-6 w-6" />
          </Button>
        </div>

        {/* Mobile drawer */}
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetContent
            side="right"
            className="w-72 border-l-[0.5px] border-navy-800 bg-navy-900 text-white [&_[data-slot=sheet-close]]:text-white [&_[data-slot=sheet-close]]:opacity-70 [&_[data-slot=sheet-close]]:hover:opacity-100"
          >
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2 text-white">
                <Image
                  src={domainConfig.logo}
                  alt={domainConfig.siteName}
                  width={drawerLogoW}
                  height={drawerLogoH}
                  className="h-8 w-auto"
                />
              </SheetTitle>
            </SheetHeader>
            <nav aria-label={t.common.mobileNavigation} className="flex flex-col gap-2 px-4">
              {navLinks.map((link) => {
                const isActive = pathname === link.href || pathname.startsWith(link.href + "/")
                return (
                  <Link
                    key={link.href}
                    href={link.href}
                    onClick={() => setMobileOpen(false)}
                    className={cn(
                      "flex min-h-12 items-center rounded-lg px-3 py-3 text-sm font-medium transition-colors",
                      isActive
                        ? "bg-white/10 text-white"
                        : "text-slate-300 hover:bg-white/5 hover:text-white"
                    )}
                  >
                    {link.label}
                  </Link>
                )
              })}
              <div className="mt-2 flex flex-col gap-2 border-t-[0.5px] border-white/10 pt-2">
                <LanguageSelector variant="mobile" />
                <a
                  href={domainConfig.instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex min-h-12 items-center gap-2 rounded-lg px-3 py-3 text-sm font-medium text-slate-300 transition-colors hover:bg-white/5 hover:text-white"
                >
                  <InstagramIcon className="h-5 w-5" />
                  Instagram
                </a>
              </div>
            </nav>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  )
}
